/**
 * Writing check wrapper API.
 * Uses gateway client with auth/tenant headers.
 * POST /v1/learning/writing/check — check grammar (wrapper); GET /v1/learning/writing/checks — list checks (future).
 */
import { AxiosError } from 'axios';
import { createHttpClient } from '@/api/http';
import { serviceUrls } from '@/config/serviceUrls';
import type { GrammarApiResponse, GrammarApiSuggestion } from '@/types/grammar';

const client = createHttpClient(serviceUrls.learning);

export interface WritingCheckRequest {
  text: string;
  max_suggestions?: number;
  task_id?: string;
  assignment_id?: string;
  submission_id?: string;
  content_id?: string;
  lesson_id?: string;
  mode?: string;
}

/** Raw response shape from backend wrapper */
export interface WritingCheckApiResponse {
  success: boolean;
  message?: string;
  data?: {
    check_id?: string;
    text?: string;
    suggestions?: GrammarApiSuggestion[] | unknown[];
    rubric?: GrammarApiResponse['rubric'];
    metadata?: Record<string, unknown>;
  };
}

export class WritingCheckError extends Error {
  code?: string;
  status?: number;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'WritingCheckError';
    this.code = code;
    this.status = status;
  }
}

function normalizeWrapperData(data: WritingCheckApiResponse['data']): GrammarApiResponse {
  const safe = data ?? {};
  const suggestions = Array.isArray(safe.suggestions) ? safe.suggestions : [];
  const rubric =
    typeof safe.rubric === 'object' && safe.rubric !== null ? safe.rubric : {};

  return {
    text: typeof safe.text === 'string' ? safe.text : '',
    suggestions: suggestions as GrammarApiResponse['suggestions'],
    rubric: rubric as GrammarApiResponse['rubric'],
  };
}

function toWritingCheckError(error: unknown): never {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    if (status === 429) {
      throw new WritingCheckError(
        'AI busy right now. Please try again shortly.',
        'QUOTA_EXHAUSTED',
        429
      );
    }

    const body = error.response?.data as WritingCheckApiResponse | undefined;
    const apiMessage =
      (body?.success === false && body?.message) ||
      (body && typeof body === 'object' && 'message' in body && typeof (body as { message?: string }).message === 'string'
        ? (body as { message: string }).message
        : null) ||
      error.message ||
      'Unable to check writing right now. Please try again.';

    throw new WritingCheckError(apiMessage, undefined, status);
  }

  if (error instanceof WritingCheckError) {
    throw error;
  }

  if (error instanceof Error) {
    throw new WritingCheckError(
      error.message || 'Unable to check writing right now. Please try again.'
    );
  }

  throw new WritingCheckError(
    'Unable to check writing right now. Please try again.'
  );
}

/**
 * Check writing via gateway wrapper. Returns normalized GrammarApiResponse for existing UI.
 */
export async function checkWriting(
  params: WritingCheckRequest
): Promise<GrammarApiResponse> {
  const body = {
    text: params.text,
    max_suggestions: params.max_suggestions ?? 8,
    task_id: params.task_id,
    assignment_id: params.assignment_id,
    submission_id: params.submission_id,
    content_id: params.content_id,
    lesson_id: params.lesson_id,
    mode: params.mode ?? 'practice',
  };

  try {
    const response = await client.post<WritingCheckApiResponse>(
      '/v1/learning/writing/check',
      body
    );

    const payload = response.data;
    if (payload?.success === false || !payload?.data) {
      throw new WritingCheckError(
        payload?.message ?? 'Unable to check writing right now. Please try again.'
      );
    }

    return normalizeWrapperData(payload.data);
  } catch (error) {
    return toWritingCheckError(error);
  }
}

/** Params for listing writing checks (history UI). Teacher/admin can filter by student_id. */
export interface GetWritingChecksParams {
  limit?: number;
  skip?: number;
  assignment_id?: string;
  submission_id?: string;
  student_id?: string;
  /** ISO date string (YYYY-MM-DD) for filtering checks from this date */
  from_date?: string;
  /** ISO date string (YYYY-MM-DD) for filtering checks until this date */
  to_date?: string;
}

/** List item shape from GET /v1/learning/writing/checks. All fields optional for defensive access. */
export interface WritingCheckListItem {
  check_id?: string;
  tenant_id?: string;
  student_id?: string;
  assignment_id?: string;
  submission_id?: string;
  task_id?: string;
  content_id?: string;
  lesson_id?: string;
  mode?: string;
  original_text?: string;
  result?: {
    text?: string;
    rubric?: {
      overallScore?: number;
      grammar?: number;
      clarity?: number;
      vocabulary?: number;
      feedback?: string;
    };
  };
  created_at?: string;
  derived_metadata?: {
    suggestion_count?: number;
    overall_score?: number;
    grammar_score?: number;
    clarity_score?: number;
    vocabulary_score?: number;
  };
  student_info?: {
    display_name?: string;
    grade?: string;
  };
}

/**
 * List writing checks for the current user (or filtered by student_id for teacher/admin).
 */
export async function getWritingChecks(
  params?: GetWritingChecksParams
): Promise<{
  items: WritingCheckListItem[];
  total?: number;
  skip?: number;
  limit?: number;
}> {
  try {
    const res = await client.get('/v1/learning/writing/checks', { params });
    const data = res.data as {
      items?: unknown[];
      total?: number;
      skip?: number;
      limit?: number;
    };
    const rawItems = Array.isArray(data?.items) ? data.items : [];
    return {
      items: rawItems as WritingCheckListItem[],
      total: data?.total,
      skip: data?.skip,
      limit: data?.limit,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const body = error.response?.data as { message?: string } | undefined;
      throw new WritingCheckError(
        body?.message ?? 'Failed to load writing checks.',
        undefined,
        error.response?.status
      );
    }
    throw error;
  }
}
